import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, Loader2, Shield } from "lucide-react";
import { loginInsurance } from "@/Calls/callInsuranceAuth";
import { useAuth } from "@/hooks/useAuth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function InsuranceLoginPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const navigate = useNavigate();
    const { setUser } = useAuth();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const res = await loginInsurance(email, password);
            if (res?.ok && res.company) {
                setUser({
                    id: res.company.company_id,
                    role: "INSURANCE",
                    page: "/insurance/dashboard",
                });
                navigate("/insurance/dashboard", { replace: true });
            } else {
                setError(res?.message || "Login failed — please check your credentials.");
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to connect to server.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden p-4 bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950">
            <div className="z-10 w-full max-w-6xl">
                <div className="bg-secondary/50 overflow-hidden rounded-[40px] shadow-2xl">
                    <div className="grid min-h-[700px] lg:grid-cols-2">
                        <div className="relative m-4 rounded-3xl bg-gradient-to-br from-blue-600 to-indigo-800 p-12 text-white flex flex-col justify-center">
                            <div className="mb-6">
                                <Shield className="h-16 w-16 text-white/80 mb-4" />
                                <h2 className="text-4xl font-bold mb-3">Insurance Portal</h2>
                                <p className="text-white/70 text-lg">
                                    Connect with customers in the IDSCRM marketplace. Receive leads, submit quotes, and issue binders directly into deal jackets.
                                </p>
                            </div>
                            <div className="space-y-3">
                                {[
                                    "Access qualified insurance leads",
                                    "Submit competitive quotes",
                                    "Issue binders directly to deals",
                                    "Integrated billing & invoicing",
                                ].map((item) => (
                                    <div key={item} className="flex items-center gap-2 text-white/80 text-sm">
                                        <div className="w-1.5 h-1.5 bg-sky-400 rounded-full" />
                                        {item}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex flex-col justify-center p-12">
                            <div className="mx-auto w-full max-w-md">
                                <div className="mb-8 text-center">
                                    <h2 className="text-3xl font-light uppercase">Insurance Login</h2>
                                    <p className="mt-2 text-sm text-muted-foreground">
                                        Sign in with your insurance company credentials
                                    </p>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div>
                                        <label htmlFor="email" className="mb-2 block text-sm font-medium uppercase">
                                            Email
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <Input
                                                id="email"
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                                className="block w-full rounded-lg border py-3 pr-3 pl-10 text-sm"
                                                placeholder="company@example.com"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="password" className="mb-2 block text-sm font-medium uppercase">
                                            Password
                                        </label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <Input
                                                id="password"
                                                type={showPassword ? "text" : "password"}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                                className="block w-full rounded-lg border py-3 pr-12 pl-10 text-sm"
                                                placeholder="Enter your password"
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="absolute right-3 top-1/2 -translate-y-1/2 h-auto p-0"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <EyeOff className="h-5 w-5 text-gray-400" /> : <Eye className="h-5 w-5 text-gray-400" />}
                                            </Button>
                                        </div>
                                    </div>

                                    {error && <p className="text-red-500 text-sm text-center">{error}</p>}

                                    <Button
                                        type="submit"
                                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-lg"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <><Loader2 className="h-5 w-5 animate-spin mr-2" />Signing in...</>
                                        ) : "Sign in to Insurance Portal"}
                                    </Button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
